/* =========================================================================
   Thermal imaging information — Laserman Technologies
   Vanilla JS, no dependencies, no build step. Scoped to #lm-thermal.

   Derived from 3d-scanning.js; the spot-size calculator is appended at the
   end of the IIFE. Edit this file directly.

   The same file backs the Shopify section and preview/index.html, so what you
   test in the preview is what runs live. The only branch is the enquiry form:
   Shopify's {% form 'contact' %} posts for real, while the preview marks its
   form with data-preview-form and this script shows the thank-you state.
   ========================================================================= */

(function () {
    'use strict';

    var root = document.getElementById('lm-thermal');
    if (!root) return;

    var entries = Array.prototype.slice.call(root.querySelectorAll('.entry'));
    var groups = Array.prototype.slice.call(root.querySelectorAll('.group'));
    var search = root.querySelector('#kb-search');
    var counter = root.querySelector('#kb-result-count');
    var noResults = root.querySelector('#kb-no-results');

    /* Cache the searchable text once, so typing stays cheap. */
    entries.forEach(function (entry) {
        entry._kbText = (entry.textContent || '').toLowerCase().replace(/\s+/g, ' ');
    });

    /* ------------------------------------------------------------ Accordion */

    function setOpen(entry, open) {
        var btn = entry.querySelector('.entry-q');
        var body = entry.querySelector('.entry-body');
        if (!btn || !body) return;
        entry.classList.toggle('open', open);
        body.hidden = !open;
        btn.setAttribute('aria-expanded', String(open));
    }

    entries.forEach(function (entry) {
        var btn = entry.querySelector('.entry-q');
        if (!btn) return;
        setOpen(entry, entry.classList.contains('open'));
        btn.addEventListener('click', function () {
            setOpen(entry, !entry.classList.contains('open'));
        });
    });

    /* --------------------------------------------------------------- Search */
    /* The section chips are jump links, not filters — search is the only thing
       that hides anything. */

    function visibleEntries() {
        return entries.filter(function (e) { return !e.hidden; });
    }

    function apply() {
        var query = (search && search.value || '').trim().toLowerCase();
        var shown = 0;

        entries.forEach(function (entry) {
            var match = !query || entry._kbText.indexOf(query) !== -1;
            entry.hidden = !match;
            if (match) shown++;
        });

        /* Hide a group heading when nothing under it survived the search. */
        groups.forEach(function (group) {
            var any = Array.prototype.slice.call(group.querySelectorAll('.entry'))
                .some(function (e) { return !e.hidden; });
            group.hidden = !any;
        });

        if (noResults) noResults.hidden = shown !== 0;

        if (counter) {
            counter.textContent = query
                ? shown + ' of ' + entries.length + ' entries match “' + query + '”.'
                : entries.length + ' entries. Search, or jump to a section above.';
        }
    }

    if (search) {
        var timer;
        search.addEventListener('input', function () {
            clearTimeout(timer);
            timer = setTimeout(apply, 120);
        });
        /* Escape clears the search rather than the browser's own reset. */
        search.addEventListener('keydown', function (e) {
            if (e.key === 'Escape') { search.value = ''; apply(); }
        });
    }

    /* ------------------------------------------------ Expand / collapse all */

    root.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-action]');
        if (!btn || !root.contains(btn)) return;
        var action = btn.dataset.action;

        if (action === 'expand') {
            visibleEntries().forEach(function (entry) { setOpen(entry, true); });
        } else if (action === 'collapse') {
            entries.forEach(function (entry) { setOpen(entry, false); });
        } else if (action === 'reset') {
            if (search) search.value = '';
            apply();
        }
    });

    /* ------------------------------------------- Jump links and deep links */

    /* Clear the theme's sticky header, if it has one. The height comes from
       --thm-header-offset in the stylesheet so there is one number to change. */
    function scrollTo(el) {
        var declared = getComputedStyle(root).getPropertyValue('--thm-header-offset');
        var offset = (parseFloat(declared) || 0) + 24;
        var top = window.pageYOffset + el.getBoundingClientRect().top - offset;
        window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
    }

    /* ---------------------------------------------------------- Compare toggle */

    function setCompare(open) {
        var btn = root.querySelector('[data-compare-toggle]');
        var body = root.querySelector('[data-compare-body]');
        if (!btn || !body) return;
        body.hidden = !open;
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        var label = btn.querySelector('[data-compare-label]');
        var icon = btn.querySelector('.compare-toggle-icon');
        if (label) label.textContent = open ? 'Hide tables' : 'Show tables';
        if (icon) icon.textContent = open ? '\u2212' : '+';
    }

    root.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-compare-toggle]');
        if (!btn || !root.contains(btn)) return;
        setCompare(btn.getAttribute('aria-expanded') !== 'true');
    });

    function reveal(id) {
        if (!id) return;
        var target;
        try { target = root.querySelector('#' + CSS.escape(id)); } catch (err) { return; }
        if (!target) return;

        /* Linking into the comparison tables opens them if they're hidden. */
        var cBody = root.querySelector('[data-compare-body]');
        if (cBody && cBody.hidden && cBody.contains(target)) setCompare(true);

        /* A search may be hiding the thing being linked to — clear it first. */
        if (target.hidden || (target.closest('.group') && target.closest('.group').hidden)) {
            if (search) search.value = '';
            apply();
        }

        if (target.classList && target.classList.contains('entry')) setOpen(target, true);

        scrollTo(target);
    }

    root.addEventListener('click', function (e) {
        var link = e.target.closest('a[href^="#"]');
        if (!link || !root.contains(link)) return;
        var id = link.getAttribute('href').slice(1);
        if (!id) return;
        var target;
        try { target = root.querySelector('#' + CSS.escape(id)); } catch (err) { return; }
        if (!target) return;
        e.preventDefault();
        if (window.history && window.history.replaceState) {
            window.history.replaceState(null, '', '#' + id);
        }
        reveal(id);
    });

    window.addEventListener('hashchange', function () {
        reveal(window.location.hash.replace('#', ''));
    });

    /* --------------------------------------------------- Missing image guard */
    /* Before `npm run images` has run (or if a source URL moves), don't show a
       broken-image icon — the figure's neutral background stands in. */

    root.querySelectorAll('.figure img, .entry-thumb img, .tile img, .flip img, .ct-photo img').forEach(function (img) {
        function fail() {
            img.style.visibility = 'hidden';
            var fig = img.closest('.figure') || img.closest('.entry-thumb');
            if (fig) fig.setAttribute('data-image-missing', '');
        }
        if (img.complete && img.naturalWidth === 0) fail();
        img.addEventListener('error', fail);
    });

    /* ---------------------------------------------------------------- Form */

    /* Live, this is Shopify's {% form 'contact' %} and the browser posts it.
       Only the preview's stand-in form is intercepted. */
    var previewForm = root.querySelector('[data-preview-form]');
    var previewSuccess = root.querySelector('[data-preview-success]');

    if (previewForm) {
        previewForm.addEventListener('submit', function (e) {
            e.preventDefault();
            if (!previewForm.reportValidity()) return;
            previewForm.hidden = true;
            if (previewSuccess) {
                previewSuccess.hidden = false;
                previewSuccess.setAttribute('tabindex', '-1');
                previewSuccess.focus();
            }
        });
    }

    /* ------------------------------------------------------- Flip cards */
    /* Each card has a front and back face stacked in one grid cell. The hidden
       face is made inert so its links can't be tabbed to; focus follows the
       flip so keyboard users land on the face they just revealed. */

    root.querySelectorAll('.flip').forEach(function (card) {
        var front = card.querySelector('.flip-front');
        var back = card.querySelector('.flip-back');
        var openBtn = front && front.querySelector('.flip-btn');
        var closeBtn = back && back.querySelector('.flip-btn-back');
        if (!front || !back || !openBtn) return;

        function setFlipped(flipped, moveFocus) {
            card.classList.toggle('flipped', flipped);
            openBtn.setAttribute('aria-expanded', String(flipped));
            if (flipped) { back.removeAttribute('inert'); front.setAttribute('inert', ''); }
            else { front.removeAttribute('inert'); back.setAttribute('inert', ''); }
            if (moveFocus) (flipped ? closeBtn : openBtn).focus({ preventScroll: true });
        }

        front.addEventListener('click', function () { setFlipped(true, document.activeElement === openBtn); });
        if (closeBtn) closeBtn.addEventListener('click', function () { setFlipped(false, true); });
        card.addEventListener('keydown', function (e) {
            if (e.key === 'Escape' && card.classList.contains('flipped')) setFlipped(false, true);
        });
        setFlipped(false, false);
    });

    /* ------------------------------------------------- Spot-size calculator */
    /* Each <option> carries "IFOV mrad|HFOV deg|VFOV deg|min focus m|max
       measuring distance m (0 = none)|short name". Pixel size in mm is simply
       IFOV x distance; the 3-pixel rule gives the smallest reliable target. */

    root.querySelectorAll('[data-thm-calc]').forEach(function (calc) {
        var model = calc.querySelector('[data-calc-model]');
        var dist = calc.querySelector('[data-calc-dist]');
        var distOut = calc.querySelector('[data-calc-dist-out]');
        var outPixel = calc.querySelector('[data-calc-pixel]');
        var outTarget = calc.querySelector('[data-calc-target]');
        var outFrame = calc.querySelector('[data-calc-frame]');
        var note = calc.querySelector('[data-calc-note]');
        if (!model || !dist) return;
        var baseNote = note ? note.textContent : '';

        function mm(v) {
            if (v >= 1000) return (v / 1000).toFixed(2).replace(/\.?0+$/, '') + ' m';
            if (v >= 100) return Math.round(v) + ' mm';
            if (v >= 10) return v.toFixed(0) + ' mm';
            return v.toFixed(1) + ' mm';
        }
        function m(v) { return v >= 10 ? v.toFixed(0) : v.toFixed(1); }

        function update() {
            var p = model.value.split('|');
            var ifov = parseFloat(p[0]), h = parseFloat(p[1]), v = parseFloat(p[2]);
            var minF = parseFloat(p[3]), maxD = parseFloat(p[4]), name = p[5];
            var d = parseFloat(dist.value);
            var px = ifov * d;
            var rad = Math.PI / 180;
            var w = 2 * d * Math.tan(h * rad / 2), ht = 2 * d * Math.tan(v * rad / 2);

            if (distOut) distOut.textContent = (d % 1 ? d.toFixed(1) : d) + ' m';
            outPixel.textContent = mm(px);
            outTarget.textContent = mm(px * 3);
            outFrame.textContent = m(w) + ' × ' + m(ht) + ' m';

            if (!note) return;
            var warn = '';
            if (minF && d < minF) warn = 'The ' + name + ' focuses from ' + minF + ' m, so at this distance the image will be out of focus.';
            else if (maxD && d > maxD) warn = 'Fluke specifies measurement with the ' + name + ' between 0.25 m and ' + maxD + ' m, so readings at this distance are outside its specification.';
            note.textContent = warn || baseNote;
            note.classList.toggle('warn', !!warn);
        }

        model.addEventListener('change', update);
        dist.addEventListener('input', update);
        update();
    });

    /* ---------------------------------------------------------------- Start */

    apply();
    reveal(window.location.hash.replace('#', ''));
})();
